"use client";

import * as React from "react";
import { Eye, EyeOff, Loader2, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/utils";
import { api } from "~/trpc/react";
import { formatHirePrice } from "~/lib/marketplace/hire-pricing";
import {
  RECOMMENDATIONS_APPOINTMENTS_OPTIONS,
  RECOMMENDATIONS_EMR_OPTIONS,
  emrIntegrationPriceCents,
  formatRecommendationsEmrLabel,
  formatRecommendationsSchedulingLabel,
  isEmrIntegrationId,
  schedulingIntegrationPriceCents,
  writeHireRecommendationsSelection,
  type HireRecommendationsSelection,
  type HireTeamHandoffCtaCluster,
  type MarketplaceEmrIntegrationId,
} from "~/lib/marketplace/hire-recommendations-pricing";
import type { MarketplaceSchedulingIntegrationId } from "~/lib/marketplace/hire-pricing";
import type { HireWizardToolCredentials } from "~/lib/marketplace/hire-wizard-draft";
import { InfoTip, LabelWithTip } from "~/components/marketplace/hire-recommendations-step";
import { writeHireCustomPreviewState } from "~/lib/marketplace/marketplace-hire-flow";
import { markHireBuilderOnboardingComplete } from "~/lib/marketplace/hire-builder-onboarding";
import { writeHireWizardContext } from "~/lib/marketplace/hire-wizard-steps";
import { startOAuthWithClerk } from "~/lib/oauth/start-oauth-with-clerk";

const SCHEDULING_PLACEHOLDER = "__select_booking_platform__";
const EMR_PLACEHOLDER = "__select_emr_platform__";
const GOOGLE_WORKSPACE_SERVER_IDS = new Set([
  "google-workspace-calendar",
  "pulsemcp-google-workspace-calendar",
]);

type CredentialField = {
  id: string;
  label: string;
  type?: "text" | "password";
  placeholder?: string;
  required?: boolean;
};

const SCHEDULING_CREDENTIAL_FIELDS: Partial<
  Record<MarketplaceSchedulingIntegrationId, CredentialField[]>
> = {
  calendly: [
    { id: "apiToken", label: "Personal Access Token", type: "password", required: true },
  ],
  zoom: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  google_calendar: [],
};

const EMR_CREDENTIAL_FIELDS: Partial<Record<MarketplaceEmrIntegrationId, CredentialField[]>> = {
  juvonno: [
    { id: "apiKey", label: "API Key", type: "password", required: true },
    {
      id: "baseUrl",
      label: "Base URL",
      placeholder: "https://tdemo.juvonno.com",
      required: true,
    },
  ],
  dentrix: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  dentrix_ascend: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  open_dental: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  eaglesoft: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  curve: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
  denticon: [{ id: "apiKey", label: "API Key", type: "password", required: true }],
};

type HireWizardToolsStepProps = {
  selection: HireRecommendationsSelection;
  onSelectionChange: (next: HireRecommendationsSelection) => void;
  credentials: HireWizardToolCredentials;
  onCredentialsChange: (next: HireWizardToolCredentials) => void;
  emrCredentialsSkipped?: boolean;
  onEmrCredentialsSkippedChange?: (skipped: boolean) => void;
  previewProjectId?: string | null;
  previewHireIntentId?: string | null;
  templateProjectId?: string | null;
  businessName?: string;
  websiteUrl?: string;
  companyProfileText?: string;
  className?: string;
};

function CredentialFields({
  integrationKey,
  fields,
  credentials,
  onCredentialsChange,
  disabled = false,
}: {
  integrationKey: string;
  fields: CredentialField[];
  credentials: HireWizardToolCredentials;
  onCredentialsChange: (next: HireWizardToolCredentials) => void;
  disabled?: boolean;
}) {
  const [showPassword, setShowPassword] = React.useState<Record<string, boolean>>({});
  const values = credentials[integrationKey] ?? {};

  if (fields.length === 0) {
    return null;
  }

  const updateField = (fieldId: string, value: string) => {
    onCredentialsChange({
      ...credentials,
      [integrationKey]: { ...values, [fieldId]: value },
    });
  };

  return (
    <div className="mt-3 space-y-3 rounded-md border border-slate-100 bg-slate-50/80 p-3">
      <p className="text-xs font-medium text-slate-700">Integration credentials</p>
      {fields.map((field) => (
        <div key={field.id} className="space-y-1.5">
          <Label htmlFor={`${integrationKey}-${field.id}`} className="text-xs">
            {field.label}
            {field.required ? " *" : ""}
          </Label>
          <div className="relative">
            <Input
              id={`${integrationKey}-${field.id}`}
              type={
                field.type === "password" && !showPassword[field.id] ? "password" : "text"
              }
              placeholder={field.placeholder}
              value={values[field.id] ?? ""}
              disabled={disabled}
              onChange={(e) => updateField(field.id, e.target.value)}
              className={field.type === "password" ? "pr-10" : undefined}
            />
            {field.type === "password" ? (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() =>
                  setShowPassword((prev) => ({ ...prev, [field.id]: !prev[field.id] }))
                }
                aria-label={showPassword[field.id] ? "Hide" : "Show"}
              >
                {showPassword[field.id] ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            ) : null}
          </div>
        </div>
      ))}
      {!disabled ? (
        <p className="text-[11px] text-slate-500">
          Credentials are saved securely to your project after you hire your agent.
        </p>
      ) : null}
    </div>
  );
}

function TeamHandoffActionsEditor({
  clusters,
  disabled,
  isGenerating,
  onChange,
  onGenerate,
  onAdd,
}: {
  clusters: HireTeamHandoffCtaCluster[];
  disabled?: boolean;
  isGenerating?: boolean;
  onChange: (next: HireTeamHandoffCtaCluster[]) => void;
  onGenerate: () => void;
  onAdd: () => void;
}) {
  const updateCluster = (index: number, patch: Partial<HireTeamHandoffCtaCluster>) => {
    const next = clusters.map((cluster, i) => (i === index ? { ...cluster, ...patch } : cluster));
    onChange(next);
  };

  const updateQuestion = (clusterIndex: number, questionIndex: number, value: string) => {
    const next = clusters.map((cluster, i) => {
      if (i !== clusterIndex) return cluster;
      const questions = [...cluster.qualificationQuestions];
      questions[questionIndex] = value;
      return { ...cluster, qualificationQuestions: questions };
    });
    onChange(next);
  };

  const addQuestion = (clusterIndex: number) => {
    const next = clusters.map((cluster, i) => {
      if (i !== clusterIndex) return cluster;
      return {
        ...cluster,
        qualificationQuestions: [...cluster.qualificationQuestions, ""],
      };
    });
    onChange(next);
  };

  const removeQuestion = (clusterIndex: number, questionIndex: number) => {
    const next = clusters.map((cluster, i) => {
      if (i !== clusterIndex) return cluster;
      return {
        ...cluster,
        qualificationQuestions: cluster.qualificationQuestions.filter(
          (_, qIndex) => qIndex !== questionIndex,
        ),
      };
    });
    onChange(next);
  };

  const removeCluster = (index: number) => {
    onChange(clusters.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {clusters.map((cluster, clusterIndex) => (
        <div
          key={`handoff-${clusterIndex}`}
          className="space-y-3 rounded-md border border-slate-100 bg-slate-50/80 p-3"
        >
          <div className="flex items-center gap-2">
            <Input
              placeholder="Team handoff action (e.g., Verify Insurance)"
              value={cluster.ctaQuestion}
              disabled={disabled}
              onChange={(e) => updateCluster(clusterIndex, { ctaQuestion: e.target.value })}
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              onClick={() => removeCluster(clusterIndex)}
              aria-label="Remove team handoff action"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-600">Follow-up questions</p>
            {cluster.qualificationQuestions.map((question, questionIndex) => (
              <div key={`q-${clusterIndex}-${questionIndex}`} className="flex items-center gap-2">
                <Input
                  placeholder="Follow-up question"
                  value={question}
                  disabled={disabled}
                  onChange={(e) => updateQuestion(clusterIndex, questionIndex, e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled || cluster.qualificationQuestions.length <= 1}
                  onClick={() => removeQuestion(clusterIndex, questionIndex)}
                  aria-label="Remove follow-up question"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              disabled={disabled}
              onClick={() => addQuestion(clusterIndex)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add question
            </Button>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || isGenerating}
          onClick={onGenerate}
        >
          {isGenerating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Wand2 className="mr-2 h-4 w-4" />
          )}
          Generate with AI
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Team Handoff Action
        </Button>
      </div>
    </div>
  );
}

export function HireWizardToolsStep({
  selection,
  onSelectionChange,
  credentials,
  onCredentialsChange,
  emrCredentialsSkipped = false,
  onEmrCredentialsSkippedChange,
  previewProjectId,
  previewHireIntentId,
  templateProjectId,
  businessName,
  websiteUrl,
  companyProfileText,
  className,
}: HireWizardToolsStepProps) {
  const utils = api.useUtils();
  const [isGeneratingHandoffAction, setIsGeneratingHandoffAction] = React.useState(false);

  const update = (patch: Partial<HireRecommendationsSelection>) => {
    onSelectionChange({ ...selection, ...patch });
  };

  const mcpConfigsQuery = api.project.getMCPConfigurations.useQuery(
    { projectId: previewProjectId! },
    { enabled: Boolean(previewProjectId) },
  );

  const deleteConfigMutation = api.project.deleteMCPConfiguration.useMutation({
    onSuccess: () => {
      toast.success("Google Calendar disconnected.");
      if (previewProjectId) {
        void utils.project.getMCPConfigurations.invalidate({ projectId: previewProjectId });
      }
    },
    onError: (error) => {
      toast.error(error.message || "Could not disconnect Google Calendar.");
    },
  });

  const googleConfig = mcpConfigsQuery.data?.find((config) =>
    GOOGLE_WORKSPACE_SERVER_IDS.has(config.serverId),
  );
  const googleCredentials = (googleConfig?.credentials as Record<string, string> | undefined) ?? {};
  const googleConnected = Boolean(
    googleConfig?.enabled && googleCredentials.googleRefreshToken?.trim(),
  );

  const schedulingSelected =
    selection.schedulingIntegrationId !== "none" &&
    !isEmrIntegrationId(selection.schedulingIntegrationId);

  const schedulingId = schedulingSelected
    ? selection.schedulingIntegrationId
    : ("none" as MarketplaceSchedulingIntegrationId);

  const emrSelected = selection.emrIntegrationId !== "none";
  const emrId = emrSelected ? selection.emrIntegrationId : ("none" as MarketplaceEmrIntegrationId);

  const appointmentsCostCents =
    selection.appointmentsEnabled && schedulingSelected
      ? schedulingIntegrationPriceCents(schedulingId)
      : 0;

  const emrCostCents =
    selection.emrIntegrationEnabled && emrSelected ? emrIntegrationPriceCents(emrId) : 0;

  const connectGoogleCalendar = () => {
    if (!previewProjectId) return;
    // Persist hire state before full-page OAuth redirect (session can be lost).
    writeHireRecommendationsSelection(selection);
    markHireBuilderOnboardingComplete();
    if (previewHireIntentId) {
      writeHireCustomPreviewState({
        previewProjectId,
        previewHireIntentId,
        ...(templateProjectId ? { templateProjectId } : {}),
      });
    }
    if (websiteUrl?.trim()) {
      writeHireWizardContext(websiteUrl, "summary");
    }
    const params = new URLSearchParams({
      projectId: previewProjectId,
      channels: "chat",
      returnTo: `${window.location.pathname}${window.location.search}`,
    });
    void startOAuthWithClerk(`/api/mcp/google-oauth/start?${params.toString()}`).catch(
      (error) => {
        toast.error(error instanceof Error ? error.message : "Google OAuth start failed");
      },
    );
  };

  const disconnectGoogleCalendar = () => {
    if (!googleConfig?.id) return;
    deleteConfigMutation.mutate({ configId: googleConfig.id });
  };

  const handleGenerateHandoffAction = async () => {
    setIsGeneratingHandoffAction(true);
    try {
      const res = await fetch("/api/marketplace/hire-wizard/generate-team-handoff-cta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessName,
          websiteUrl,
          context: companyProfileText,
          existingLabels: selection.teamHandoffCtaClusters.map((cluster) => cluster.ctaQuestion),
        }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Could not generate team handoff action.");
      }
      const generated = (await res.json()) as HireTeamHandoffCtaCluster;
      update({
        teamHandoffCtaClusters: [...selection.teamHandoffCtaClusters, generated],
      });
      toast.success("Team handoff action generated.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not generate team handoff action.");
    } finally {
      setIsGeneratingHandoffAction(false);
    }
  };

  const handleAddHandoffAction = () => {
    update({
      teamHandoffCtaClusters: [
        ...selection.teamHandoffCtaClusters,
        {
          ctaQuestion: "",
          qualificationQuestions: [""],
          locationRouting: "all",
        },
      ],
    });
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div>
        <h3 className="text-base font-semibold text-slate-900">Tools &amp; Integrations</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect scheduling and practice management tools for your Inbound GPT Agent.
        </p>
      </div>

      <div className="space-y-5 rounded-lg border border-slate-200 p-4">
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-slate-900">
              <LabelWithTip
                label="Appointments and Scheduling"
                showTooltip
                tooltip="Connect Google Calendar, Calendly, or Zoom so your agent can book appointments."
              />
            </h4>
            <Switch
              checked={selection.appointmentsEnabled}
              onCheckedChange={(appointmentsEnabled) =>
                update({
                  appointmentsEnabled,
                  schedulingIntegrationId: appointmentsEnabled
                    ? selection.schedulingIntegrationId === "none" ||
                      isEmrIntegrationId(selection.schedulingIntegrationId)
                      ? "google_calendar"
                      : selection.schedulingIntegrationId
                    : "none",
                })
              }
              aria-label="Enable appointments and scheduling"
            />
          </div>
          {selection.appointmentsEnabled ? (
            <>
              <Select
                value={schedulingSelected ? schedulingId : SCHEDULING_PLACEHOLDER}
                onValueChange={(next) => {
                  if (next === SCHEDULING_PLACEHOLDER) return;
                  update({
                    schedulingIntegrationId: next as MarketplaceSchedulingIntegrationId,
                  });
                }}
              >
                <SelectTrigger className="h-10 w-full text-sm">
                  <SelectValue placeholder="Select booking platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SCHEDULING_PLACEHOLDER} disabled>
                    Select booking platform
                  </SelectItem>
                  {RECOMMENDATIONS_APPOINTMENTS_OPTIONS.map((integration) => (
                    <SelectItem key={integration.id} value={integration.id}>
                      {formatRecommendationsSchedulingLabel(integration.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {appointmentsCostCents > 0 ? (
                <p className="mt-2 text-xs text-slate-500">
                  Tool add-on: {formatHirePrice(appointmentsCostCents)}/mo
                </p>
              ) : null}
              {schedulingSelected && schedulingId === "google_calendar" ? (
                <div className="mt-3 rounded-md border border-slate-100 bg-slate-50/80 p-3">
                  <p className="text-xs font-medium text-slate-700">Google Calendar</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Connect your Google account so your agent can check availability and book
                    appointments.
                  </p>
                  {googleConnected && googleCredentials.accountEmail ? (
                    <p className="mt-2 text-xs text-emerald-700">
                      Connected as {googleCredentials.accountEmail}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant={googleConnected ? "outline" : "default"}
                    className="mt-3"
                    disabled={
                      !previewProjectId ||
                      deleteConfigMutation.isPending ||
                      mcpConfigsQuery.isLoading
                    }
                    onClick={googleConnected ? disconnectGoogleCalendar : connectGoogleCalendar}
                  >
                    {deleteConfigMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {googleConnected ? "Disconnect from Google" : "Connect to Google Account"}
                  </Button>
                  {!previewProjectId ? (
                    <p className="mt-2 text-[11px] text-slate-500">
                      Available after your agent preview is generated on the Summary step.
                    </p>
                  ) : null}
                </div>
              ) : null}
              {schedulingSelected ? (
                <CredentialFields
                  integrationKey={`scheduling:${schedulingId}`}
                  fields={SCHEDULING_CREDENTIAL_FIELDS[schedulingId] ?? []}
                  credentials={credentials}
                  onCredentialsChange={onCredentialsChange}
                />
              ) : null}
            </>
          ) : null}
        </div>

        <div className="border-t border-slate-100 pt-5">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-slate-900">
              <LabelWithTip
                label="Practice Management System (EMR)"
                showTooltip
                tooltip="EMR and practice management for patient lookup and appointment booking."
              />
            </h4>
            <Switch
              checked={selection.emrIntegrationEnabled}
              onCheckedChange={(emrIntegrationEnabled) =>
                update({
                  emrIntegrationEnabled,
                  emrIntegrationId:
                    emrIntegrationEnabled && selection.emrIntegrationId === "none"
                      ? "none"
                      : selection.emrIntegrationId,
                })
              }
              aria-label="Enable EMR integration"
            />
          </div>
          {selection.emrIntegrationEnabled ? (
            <>
              <Select
                value={emrSelected ? emrId : EMR_PLACEHOLDER}
                onValueChange={(next) => {
                  if (next === EMR_PLACEHOLDER) return;
                  update({ emrIntegrationId: next as MarketplaceEmrIntegrationId });
                }}
              >
                <SelectTrigger className="h-10 w-full text-sm">
                  <SelectValue placeholder="Select Practice Management System" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMR_PLACEHOLDER} disabled>
                    Select Practice Management System
                  </SelectItem>
                  {RECOMMENDATIONS_EMR_OPTIONS.map((integration) => (
                    <SelectItem key={integration.id} value={integration.id}>
                      {formatRecommendationsEmrLabel(integration.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {emrCostCents > 0 ? (
                <p className="mt-2 text-xs text-slate-500">
                  Tool add-on: {formatHirePrice(emrCostCents)}/mo
                </p>
              ) : null}
              {emrSelected ? (
                <>
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50/80 px-3 py-2">
                    <div>
                      <p className="text-xs font-medium text-slate-700">EMR credentials</p>
                      <p className="text-[11px] text-slate-500">
                        Required to connect your practice management system.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={() => onEmrCredentialsSkippedChange?.(!emrCredentialsSkipped)}
                    >
                      {emrCredentialsSkipped ? "Add credentials now" : "Skip — add later"}
                    </Button>
                  </div>
                  <CredentialFields
                    integrationKey={`emr:${emrId}`}
                    fields={EMR_CREDENTIAL_FIELDS[emrId] ?? EMR_CREDENTIAL_FIELDS.juvonno ?? []}
                    credentials={credentials}
                    onCredentialsChange={onCredentialsChange}
                    disabled={emrCredentialsSkipped}
                  />
                </>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="border-t border-slate-100 pt-5">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-slate-900">
              <LabelWithTip
                label="Team Handoff Actions"
                showTooltip
                tooltip="Button labels and follow-up questions when visitors want to connect with your team."
              />
            </h4>
            <Switch
              checked={selection.teamHandoffEnabled}
              onCheckedChange={(teamHandoffEnabled) => update({ teamHandoffEnabled })}
              aria-label="Enable team handoff actions"
            />
          </div>
          {selection.teamHandoffEnabled ? (
            <TeamHandoffActionsEditor
              clusters={selection.teamHandoffCtaClusters}
              isGenerating={isGeneratingHandoffAction}
              onChange={(teamHandoffCtaClusters) => update({ teamHandoffCtaClusters })}
              onGenerate={handleGenerateHandoffAction}
              onAdd={handleAddHandoffAction}
            />
          ) : (
            <p className="text-xs text-slate-500">
              Enable team handoff so visitors can request callbacks, speak with staff, or verify
              insurance.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
